import { findStoryElement, isStoryElementReference } from 'utils/blocks/story-element-reference';
import produce from 'immer';
import { get, set, isEmpty } from 'lodash';
import { lruMemoize } from 'reselect';
import type {
	TreeModel,
	BBSymbolLink,
	CardData,
	CardEditorType,
	SelectedBBSingle,
	StoryElementReference,
	StorySymbols,
} from 'types/story';
import { getInstanceByRef, getNestedSymbols, isSymbol } from 'utils/blocks/symbol';
import { findComponentBy } from 'utils/blocks/find-component-by';
import { getSymbolModel } from 'utils/blocks/get-symbol-model';

// get element by 'id'. used as more expensive, but robust fallback, instead of get by 'path'
function findById(collection: TreeModel[], id: string) {
	return findComponentBy(collection, { path: '_id', value: id })?.component;
}

function getSelectedSymbolModel(props: { symbol: BBSymbolLink; symbols: StorySymbols; elementId: string }) {
	// Selection arrives asynchronously from the iframe and may still refer to a detached instance.
	if (!getInstanceByRef(props.symbol.symbol, props.symbols)) return undefined;
	const fullSymbolModel = getSymbolModel(props.symbol, props.symbols);
	return fullSymbolModel ? findById([fullSymbolModel], props.elementId) : undefined;
}

type GetSelectedElementModelProps = {
	element: SelectedBBSingle;
	cardData: CardData;
	symbols?: StorySymbols;
	storyElements: CardEditorType['storyElements'];
};

// Get entire original model of selected element
function getSelectedElementModelFn(props: GetSelectedElementModelProps): {
	model: TreeModel | undefined;
	hasNestedSymbols: boolean;
} {
	const { path, _id } = props.element;
	const hasSymbols = !isEmpty(props.symbols);
	const result: ReturnType<typeof getSelectedElementModelFn> = {
		model: undefined,
		hasNestedSymbols: false,
	};

	// story element
	const canonical = findStoryElement(props.storyElements, _id);
	if (canonical) {
		result.model = canonical.component;
		return result;
	}

	// symbol
	if (isSymbol(props.element) && hasSymbols) {
		const symbolModel = getSelectedSymbolModel({
			symbol: props.element,
			symbols: props.symbols!,
			elementId: _id,
		});

		if (symbolModel) {
			result.model = symbolModel;
			return result;
		}
	}

	let el = get(props.cardData.elements, path) as TreeModel | BBSymbolLink | StoryElementReference | undefined;
	if (!el || isStoryElementReference(el) || ('_id' in el ? el._id !== _id : el.symbol.instanceId !== _id)) {
		el = findById(props.cardData.elements, _id);
	}
	// Undo can restore a link before the iframe has restored the selection's symbol metadata.
	if (el && isSymbol(el)) {
		result.model = hasSymbols
			? getSelectedSymbolModel({ symbol: el, symbols: props.symbols!, elementId: _id })
			: '_id' in el
				? el
				: undefined;
		return result;
	}
	if (!el && hasSymbols) {
		for (const { component } of getNestedSymbols(props.cardData.elements)) {
			const model = getSelectedSymbolModel({ symbol: component, symbols: props.symbols!, elementId: _id });
			if (model) {
				result.model = model;
				return result;
			}
		}
	}

	// collect models of nested symbols
	if (el && el?.children && hasSymbols) {
		getNestedSymbols(el.children).forEach(nestedSymbol => {
			if (nestedSymbol.component.symbol.childId !== undefined) return;
			if (!result.hasNestedSymbols) {
				result.hasNestedSymbols = true;
			}

			el = produce(el, draft => {
				const symbolModel = getSelectedSymbolModel({
					symbol: nestedSymbol.component,
					symbols: props.symbols!,
					elementId: nestedSymbol.component.symbol.instanceId,
				});
				// replace child symbol link with a symbol instance model
				if (symbolModel && draft) set(draft, `children.${nestedSymbol.path}`, symbolModel);
			});
		});
	}

	// simple card element
	result.model = el;

	return result;
}

const isEqual = (a: GetSelectedElementModelProps, b: GetSelectedElementModelProps) =>
	a.element === b.element &&
	a.cardData === b.cardData &&
	a.storyElements === b.storyElements &&
	a.symbols === b.symbols;

const memoizedGetSelectedElementModel = lruMemoize(getSelectedElementModelFn, isEqual);

export const getSelectedElementModel = memoizedGetSelectedElementModel;
